import ContactUs from 'components/contact-us';
import {
  ChevronDown as IconDownward,
  ChevronRight as IconRight,
} from 'react-feather';
import { useState } from 'react';
import RichText from 'components/rich-text';
import { PageTitle, SmallHeader, ParagraphText } from 'components/typography';
import { css, Theme } from '@emotion/react';
import faqs from 'resources/data/faq-data.json';
import { Container, Box, Grid } from 'components/box';
import { Accordion, AccordionItem } from 'components/_shared/widgets/accordion';
import MenuLinks from 'components/menu-links';
import { Link } from 'components/typography';
import { ReactGaTrackCategory, track } from '../util/track';
import { useVodapayActive } from 'hooks/app';
import config from '../util/load-config';

interface QuestionType {
  question: string;
  answer: string;
}

const svgStyles = css`
  height: 18px;
  flex-basis: 24px;
  flex-shrink: 0;
`;

const linkStyles = (theme: Theme) => css`
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary};
  font-family: ${theme.fonts.header};
  cursor: pointer;
`;

const headerStyles = (theme: Theme) => css`
  font-family: ${theme.fonts.header};
  font-weight: 700;
`;

const richTextStyles = (theme: Theme) => css`
  color: ${theme.colors.black};
  font-weight: 400;
  line-height: 1.5em;
  @media ${theme.mediaQueries.desktopOnly} {
    font-size: ${theme.fontSizes.m};
    line-height: 1.5em;
  }
  @media ${theme.mediaQueries.tabletOnly} {
    font-size: 1.2rem;
  }
  a {
    color: ${theme.colors.link};
    font-weight: 600;
  }

  p,
  ul {
    margin-bottom: 12px;
  }
  ul {
    list-style-type: disc;
    padding-left: 20px;
  }
  p:last-of-type {
    margin-bottom: 0px;
  }
`;

const Question = ({
  question,
  index,
  isOpen,
  setOpenQuestionIndex,
}: {
  question: QuestionType;
  index: number;
  isOpen: boolean;
  setOpenQuestionIndex: (index: number | null) => void;
}) => (
  <div
    css={theme => css`
      font-size: ${theme.fontSizes.m};
      margin: ${theme.space[1]}px 0;
      padding: 0 ${theme.space[2]}px 0 ${theme.space[1]}px;
    `}
  >
    <button
      css={theme => css`
        color: ${theme.colors.darkGrey};
        display: flex;
        align-items: center;
        font-weight: 700;
        &:active,
        &:focus {
          outline: 0;
          box-shadow: none !important;
        }
        font-family: ${theme.fonts.header};
        font-size: ${theme.fontSizes.regular};
        text-align: left;
        @media ${theme.mediaQueries.tabletOnly} {
          font-size: 1.2rem;
        }
      `}
      type="button"
      onClick={() =>
        isOpen ? setOpenQuestionIndex(null) : setOpenQuestionIndex(index)
      }
    >
      {isOpen ? (
        <IconDownward css={svgStyles} />
      ) : (
        <IconRight css={svgStyles} />
      )}
      <span
        css={theme => css`
          @media ${theme.mediaQueries.mobileOnly} {
            text-align: left;
          }
        `}
      >
        {question.question}
      </span>
    </button>

    {isOpen && (
      <RichText
        fontSize="regular"
        content={question.answer}
        ml={4}
        mt={3}
        css={richTextStyles}
      />
    )}
  </div>
);

interface FAQ {
  title: string;
  questions: { question: string; answer: string }[];
}

const Faq = () => {
  const [openQuestionIndex, setOpenQuestionIndex] = useState<null | number>(
    null
  );
  const isVodapayActive = useVodapayActive();
  const sections: FAQ[] = isVodapayActive ? faqs.vodapay : faqs.web;

  return (
    <Container>
      <Grid
        gridTemplateColumns={['1fr', null, '1fr 3fr 2fr']}
        gridColumnGap={[0, null, 4]}
      >
        <MenuLinks title="FAQ" slug="faq" />
        <Box>
          <Accordion>
            <Grid>
              <PageTitle>Frequently Asked Questions</PageTitle>

              <SmallHeader>When will I get my order?</SmallHeader>
              <ParagraphText fontSize="regular">
                Your order will be shipped within 5-10 working days, unless
                otherwise specified. See your{' '}
                <Link href="https://onedayonly.co.za/my-orders">My Orders</Link>{' '}
                page for the very latest details we have, but rest assured, as
                soon as your order is ready for dispatch, we&apos;ll send you a
                mail with the tracking info.{' '}
                <Link
                  href="/about-shipping"
                  role="button"
                  tabIndex={-3}
                  onKeyDown={() =>
                    track(
                      'Read More - When will I get order',
                      ReactGaTrackCategory.faq
                    )
                  }
                  onClick={() =>
                    track(
                      'Read More - When will I get order',
                      ReactGaTrackCategory.faq
                    )
                  }
                  css={linkStyles}
                >
                  READ MORE
                </Link>
              </ParagraphText>

              <SmallHeader>Can I return a product?</SmallHeader>
              <ParagraphText fontSize="regular">
                Yes! You can log a return via your{' '}
                <Link href="https://onedayonly.co.za/my-orders">My Orders</Link>{' '}
                page. Select the relevant order, hit the big &apos;log a
                return&apos; button at the bottom of the order page and follow
                the steps to submit. Once submitted, our customer service team
                will evaluate your return for eligibility before it is
                processed.{' '}
                <Link
                  target="_blank"
                  href={config.helpCentreLinks.aboutReturns}
                  role="button"
                  tabIndex={-5}
                  onKeyDown={() =>
                    track('Read More - Can I return', ReactGaTrackCategory.faq)
                  }
                  onClick={() =>
                    track('Read More - Can I return', ReactGaTrackCategory.faq)
                  }
                  css={linkStyles}
                >
                  READ MORE
                </Link>
              </ParagraphText>

              {sections.map((section, index) => (
                <AccordionItem key={index} title={section.title}>
                  {section.questions.map((question, questionIndex) => (
                    <Question
                      key={`${index}-${questionIndex}`}
                      question={question}
                      index={questionIndex}
                      setOpenQuestionIndex={setOpenQuestionIndex}
                      isOpen={openQuestionIndex === questionIndex}
                    />
                  ))}
                </AccordionItem>
              ))}
            </Grid>
          </Accordion>
        </Box>
        <Box>
          <ContactUs isFaq />
        </Box>
      </Grid>
    </Container>
  );
};

export default Faq;
